import { Loading3QuartersOutlined } from '@ant-design/icons';
import { useGetFrontRichiesteId } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { useGetFrontTipiDomandaId } from '@framework/api-client/api/inquiries-compile/tipi-domanda/tipi-domanda.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { selectTranslatedInquiryType } from '@framework/common-web/schema/translations/selectTranslatedInquiryType.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { OnPageResultContainer } from '@framework/ui/components/alert/OnPageResultContainer.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  generatePath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Alert, Result } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import styled, { useTheme } from 'styled-components';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import { LoadingPage } from '../loading/LoadingPage.tsx';

const IconColorStyle = styled.span`
  color: ${props => props.theme.colorSuccess};
`;

interface Params {
  id: string;
}

export interface ISubmissionResultPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina di riepilogo che mostra lo stato della richiesta una volta inviata.
 * Può mostrare il risultato dopo l'invio o lo stato di invio in corso.
 */
export function SubmissionResultPage(props: ISubmissionResultPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');

  const history = useHistory();
  const { t } = useTranslation();

  const {
    data: submission,
    isLoading,
    error,
  } = useGetFrontRichiesteId(submissionId!, {
    query: { keepPreviousData: true, enabled: Boolean(submissionId) },
  });

  const inquiryRequest = useGetFrontTipiDomandaId(
    submission?.idTipoDomanda ?? '<unknown>',
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submission?.idTipoDomanda),
        select: selectTranslatedInquiryType,
      },
    },
  );

  // Breadcrumbs
  useBreadcrumbItem({
    path: '/richieste',
    title: t('submission-list.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: inquiryRequest.data?.titolo ?? '',
  });

  const sentLayout = useMemo(
    () => inquiryRequest.data?.postCompilazione?.inviato,
    [inquiryRequest.data?.postCompilazione?.inviato],
  );
  const theme = useTheme();

  const handlePrev = useCallback(() => {
    // TODO
    console.log('TODO');
  }, []);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={getNetworkErrorMessage(
            error,
            t(
              'submission-form.errore-in-caricamento-richiesta',
              'Si è verificato un errore durante il caricamento della richiesta.',
            ),
          )}
        />
      </PageLayout>
    );
  }

  if (!submissionId) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={t(
            'submission-form.id-della-richiesta-non-valido',
            'ID della richiesta non valido',
          )}
        />
      </PageLayout>
    );
  }

  if (SubmissionLogic.isSubmissionSent(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/inviata', { id: submissionId }),
    );
  }

  return (
    <PageLayout>
      <ResponsiveCard>
        <OnPageResultContainer>
          {submission?.stato === EnumRichiestaDTOStato.IN_INVIO ? (
            <Result
              icon={
                <Loading3QuartersOutlined
                  spin
                  style={{ color: theme.colorSuccess }}
                  aria-label="Invio in corso..."
                />
              }
              title={t('submission-form.invio-in-corso', 'Invio in corso...')}
            />
          ) : (
            <span /> // TODO
          )}
        </OnPageResultContainer>
      </ResponsiveCard>
    </PageLayout>
  );
}
